import {cats} from "./setup";
import {ICategoryItem} from "../interfaces/ICategoryItem";
import {EFilterName} from "../services/IconService";
import {appConfigService} from "../services/AppConfigService";
import {key} from "ionicons/icons";

interface IModalSetting {
    initialBreakPoint: number,
    breakpoints: number[]
}

export interface IAppConfig {

    // The base url of the app, without /
    appBaseUrl: string
    languageOn: boolean
    srs: number
    kommunenr: number[]
    filterName: EFilterName
    off_kode: number // Publication name

    // A facility can be shared from the modal
    shareFacility: boolean;

    // Only points of these types will be retrieved from the GeoFA service
    FACILITY_POINTS: number[];
    FACILITY_LINES: number[];
    FACILITY_POLYGONS: number[];

    // Set this one if you run the bbox and need to filter anything outside the kommunegrænse away.
    // Like so: '/blabla.json' NB: takes only json so rename geojsons. Needs to be in 4326 and put it into /public
    kommunepolygonFileName?: string

    // Path to a static GeoJSON file to load as an additional layer (relative to public folder or absolute URL)
    // Example: '/assets/custom-layer.geojson'
    geoJsonLayerUrl?: string
    // Projection of the GeoJSON file (e.g., 'EPSG:4326', 'EPSG:3857', 'EPSG:25832')
    // If not specified, will try to auto-detect from coordinate values
    geoJsonLayerProjection?: string

    // Use the bbox method to retrieve data
    bbox: number[][] | boolean
    apiBaseUrl: string
    photoBaseUrl: string
    visitDenmark: boolean
    map: {
        zoomToExtent?: boolean,
        initialMapCenter: number[],
        initialMapZoom: number,
        hitTolerance: number,
        enableRotation: boolean,
        scaleBar: {
            minWidth: number,
            maxWidth: number,
            maxHeight: number
        }
    }
    categories: ICategoryItem[]
    modals: {
        filter: IModalSetting,
        list: IModalSetting,
        mapClick: IModalSetting
    },
    swipers: {
        detail: {
            slidesPerView: number
        },
        photos: {
            slidesPerView: number
        }
    },
    distanceTappedLocation: number          // The distance between the current location and the one we have
                                            // tapped the orientation FAB last
}

// Default fallback config (used if JSON config not loaded yet)
const defaultConfig: IAppConfig = {
    appBaseUrl: 'https://app.skjoldunger.nordiq-gis.dk',
    languageOn: true,
    kommunenr: [265, 350, 250],
    filterName: EFilterName.beliggenhedskommune,
    off_kode: 1,
    shareFacility: false,
    FACILITY_POINTS: [1022, 1031, 1012, 1041, 1051, 1061, 1062, 1072, 1082, 1091, 1102, 1182, 1191,
        1122, 1132, 1142, 1152, 1201, 1211, 1222, 1232, 1291, 1302, 1331, 1351, 2012, 2022, 2031, 2041, 2051, 2062,
        2072, 2082, 2092, 2171, 2132, 2201, 2223, 3012, 3031, 3041, 3051, 3061, 3071, 3091, 4041, 4051,
        4081, 4101, 4111, 4121, 4132, 4142, 4222, 4231, 4322, 4272, 4071, 4161, 4191, 4411, 5011, 5021,
        5031, 5051, 5061, 5072, 5082, 5102, 6011, 6052, 6062, 6042, 9981],
    FACILITY_LINES: [],
    FACILITY_POLYGONS: [],
    kommunepolygonFileName: './550-simpilified-100m-900913.json',
    geoJsonLayerUrl: '/assets/delineation/skjoldung.geojson',
    geoJsonLayerProjection: 'EPSG:4326',
    srs: 3857,
    bbox: false,
    apiBaseUrl: "https://geofa.geodanmark.dk/api/v2/sql/fkg",
    photoBaseUrl: "https://mapcentia-www.s3-eu-west-1.amazonaws.com/fkg/",
    visitDenmark: false,
    map: {
        zoomToExtent: false,
        initialMapCenter: [11.725618, 55.491737],
        initialMapZoom: 8,
        hitTolerance: 10,
        enableRotation: false,
        scaleBar: {
            minWidth: 200,
            maxWidth: 200,
            maxHeight: 30,
        }
    },
    categories: cats,
    modals: {
        filter: {
            initialBreakPoint: 0.7,
            breakpoints: [0, 0.7, 1]
        },
        list: {
            initialBreakPoint: 0.4,
            breakpoints: [0, 0.4, 1]
        },
        mapClick: {
            initialBreakPoint: 0.4,
            breakpoints: [0, 0.4, 1]
        },
    },
    swipers: {
        detail: {
            slidesPerView: 1.1
        },
        photos: {
            slidesPerView: 1.1
        }
    },
    distanceTappedLocation: 100
};

// Create a Proxy that reads from appConfigService when available, falls back to defaultConfig
export const AppConfig = new Proxy(defaultConfig, {
    get(target, prop: keyof IAppConfig) {
        // Try to get from loaded config first
        const loadedConfig = appConfigService.config;
        console.log('==> AppConfig', prop, key, loadedConfig);
        if (loadedConfig && prop in loadedConfig) {
            return loadedConfig[prop];
        }
        // Fall back to default config
        return target[prop];
    }
}) as IAppConfig;
